import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { api, formatPrice, rentLabel } from "@/lib/api";
import ContactForm from "@/components/ContactForm";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const CATEGORY_LABEL = {
  transitorio: "Contratto transitorio",
  residenziale: "Locazione di medio-lungo periodo",
  studenti: "Studenti e professionisti",
};

export default function PropertyDetail() {
  const { code } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setP(null);
    setNotFound(false);
    setActive(0);
    api.get(`/properties/${code}`)
      .then((r) => setP(r.data))
      .catch(() => setNotFound(true));
  }, [code]);

  if (notFound) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 pt-28 text-center">
        <p className="font-serif text-4xl italic">Immobile non trovato.</p>
        <Link to="/vendita" className="mt-8 text-[12px] uppercase tracking-wide2 text-champagne underline underline-offset-4">
          Torna alla vendita
        </Link>
      </div>
    );
  }

  if (!p) {
    return (
      <div className="mx-auto max-w-[1440px] px-6 pt-32 lg:px-12">
        <div className="aspect-[16/9] animate-pulse bg-sand3" />
      </div>
    );
  }

  const isSale = p.status === "vendita";
  const specs = [
    ["Tipologia", p.typology],
    ["Superficie", `${p.surface} m²`],
    ["Vani", String(p.rooms)],
    ["Camere", String(p.bedrooms)],
    ["Bagni", String(p.bathrooms)],
    ["Piano", p.floor],
    ["Classe energetica", p.energy_class],
    ["Disponibilità", p.availability || "—"],
  ];

  return (
    <div data-testid="property-detail-page" className="pt-28 lg:pt-32">
      <Seo
        title={`${p.title} — IDEAL REAL ESTATE`}
        description={`${p.typology} con ${p.surface} m², ${p.rooms} vani, piano ${p.floor}. Contattaci per informazioni o per prenotare una visita.`}
        path={`/immobile/${p.code}`}
      />
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <Link
          to={isSale ? "/vendita" : "/locazione"}
          data-testid="property-back-link"
          className="inline-flex items-center gap-2 text-[12px] font-medium uppercase tracking-wide2 text-mut transition-colors hover:text-champagne"
        >
          <ArrowLeft className="h-4 w-4" />
          {isSale ? "Immobili in vendita" : "Immobili in locazione"}
        </Link>

        <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            {(p.zone || p.city || p.ref) && (
              <p className="font-mono text-[12px] uppercase tracking-[0.2em] text-champagne">
                {[p.zone, p.city].filter(Boolean).join(" · ")}{p.ref ? `${(p.zone || p.city) ? " — " : ""}Rif. ${p.ref}` : ""}
              </p>
            )}
            <h1 className="mt-3 max-w-3xl font-serif text-4xl font-medium leading-tight lg:text-6xl">{p.title}</h1>
          </div>
          {isSale && (
            <p className="shrink-0 font-mono text-xl font-medium text-ink lg:text-2xl" data-testid="property-price">
              {formatPrice(p)}
            </p>
          )}
        </div>
      </div>

      {/* Galleria */}
      <div className="mx-auto mt-10 max-w-[1440px] px-6 lg:px-12">
        <div className="overflow-hidden bg-sand3">
          <img
            src={p.images[active]}
            alt={`${p.title} — foto ${active + 1}`}
            className="tone-img aspect-[16/9] w-full object-cover"
            data-testid="property-main-image"
          />
        </div>
        <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
          {p.images.map((src, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              data-testid={`property-thumb-${i}`}
              className={`shrink-0 overflow-hidden border-2 transition-colors duration-300 ${i === active ? "border-champagne" : "border-transparent opacity-70 hover:opacity-100"}`}
              aria-label={`Foto ${i + 1}`}
            >
              <img src={src} alt="" className="tone-img h-20 w-28 object-cover lg:h-24 lg:w-36" />
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto grid max-w-[1440px] gap-16 px-6 py-20 lg:grid-cols-12 lg:px-12 lg:py-28">
        {/* Descrizione e dati */}
        <div className="lg:col-span-7">
          <Reveal>
            <div className="grid grid-cols-2 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-4">
              {specs.map(([label, value]) => (
                <div key={label} className="bg-sand p-5">
                  <p className="text-[10px] uppercase tracking-wide2 text-mut">{label}</p>
                  <p className="mt-2 font-mono text-[15px] font-medium text-ink">{value}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="mt-16 font-serif text-3xl">Descrizione</h2>
            <p className="mt-6 text-base font-light leading-relaxed text-ink2">{p.description}</p>
          </Reveal>

          {!isSale && p.conditions && (
            <Reveal delay={0.15}>
              <h2 className="mt-14 font-serif text-3xl">Condizioni contrattuali</h2>
              <div className="mt-6 border-l-2 border-champagne bg-sand2 p-6">
                <p className="text-[15px] font-light leading-relaxed text-ink2">{p.conditions}</p>
              </div>
            </Reveal>
          )}

          {p.features?.length > 0 && (
            <Reveal delay={0.2}>
              <h2 className="mt-14 font-serif text-3xl">Caratteristiche</h2>
              <ul className="mt-6 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[15px] font-light text-ink2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-champagne" />
                    {f}
                  </li>
                ))}
              </ul>
            </Reveal>
          )}
        </div>

        {/* Form visita */}
        <div className="lg:col-span-5">
          <div className="border border-ink/10 bg-sand2 p-8 lg:sticky lg:top-28 lg:p-10" data-testid="property-inquiry-card">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne">
              {isSale ? "Visita o informazioni" : "Informazioni e disponibilità"}
            </p>
            <h3 className="mt-3 font-serif text-3xl leading-snug">
              {isSale ? "Prenota una visita" : "Richiedi informazioni"}
            </h3>
            <p className="mt-3 text-sm font-light text-mut">
              Ti ricontattiamo entro un giorno lavorativo.
            </p>
            <div className="mt-8">
              <ContactForm variant="visita" property={p} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
